/* eslint-disable */

import { bezierInterpolation } from './interpolations';

export type EasingFunction = (progress: number) => number;

interface TweenType {
  In: EasingFunction;
  Out: EasingFunction;
  InOut: EasingFunction;
}

export class Easing {
  public static Default: EasingFunction;

  public static Power0: EasingFunction;

  public static Power1: EasingFunction;

  public static Power2: EasingFunction;

  public static Power3: EasingFunction;

  public static Power4: EasingFunction;

  public static Linear: { None: EasingFunction } = {
    None: (k: number): number => k,
  };

  public static Quadratic: TweenType = {
    In: (k: number): number => k * k,
    Out: (k: number): number => k * (2 - k),
    InOut: (k: number): number => ((k *= 2) < 1 ? 0.5 * k * k : -0.5 * (--k * (k - 2) - 1)), // tslint:disable-line
  };

  public static Cubic: TweenType = {
    In: (k: number): number => k * k * k,
    Out: (k: number): number => --k * k * k + 1,
    InOut: (k: number): number => {
      if ((k *= 2) < 1) {
        return 0.5 * k * k * k;
      } // tslint:disable-line
      return 0.5 * ((k -= 2) * k * k + 2);
    },
  };

  public static Quartic: TweenType = {
    In: (k: number): number => k * k * k * k,
    Out: (k: number): number => 1 - --k * k * k * k,
    InOut: (k: number): number => {
      if ((k *= 2) < 1) {
        return 0.5 * k * k * k * k;
      } // tslint:disable-line
      return -0.5 * ((k -= 2) * k * k * k - 2);
    },
  };

  public static Quintic: TweenType = {
    In: (k: number): number => k * k * k * k * k,
    Out: (k: number): number => --k * k * k * k * k + 1,
    InOut: (k: number): number => {
      if ((k *= 2) < 1) {
        return 0.5 * k * k * k * k * k;
      } // tslint:disable-line
      return 0.5 * ((k -= 2) * k * k * k * k + 2);
    },
  };

  public static Sinusoidal: TweenType = {
    In: (k: number): number => {
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      return 1 - Math.cos((k * Math.PI) / 2);
    },
    Out: (k: number): number => {
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      return Math.sin((k * Math.PI) / 2);
    },
    InOut: (k: number): number => {
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      return 0.5 * (1 - Math.cos(Math.PI * k));
    },
  };

  public static Exponential: TweenType = {
    In: (k: number): number => (k === 0 ? 0 : 1024 ** (k - 1)),
    Out: (k: number): number => (k === 1 ? 1 : 1 - 2 ** (-10 * k)),
    InOut: (k: number): number => {
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      if ((k *= 2) < 1) {
        return 0.5 * 1024 ** (k - 1);
      } // tslint:disable-line
      return 0.5 * (-(2 ** (-10 * (k - 1))) + 2);
    },
  };

  public static Circular: TweenType = {
    In: (k: number): number => 1 - Math.sqrt(1 - k * k),
    Out: (k: number): number => Math.sqrt(1 - --k * k),
    InOut: (k: number): number => {
      if ((k *= 2) < 1) {
        return -0.5 * (Math.sqrt(1 - k * k) - 1);
      } // tslint:disable-line
      return 0.5 * (Math.sqrt(1 - (k -= 2) * k) + 1);
    },
  };

  public static Elastic: TweenType = {
    In: (k: number): number => {
      const a = 1;
      const p = 0.4;
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      const s: number = p / 4;
      return -(a * 2 ** (10 * (k -= 1)) * Math.sin(((k - s) * (2 * Math.PI)) / p));
    },
    Out: (k: number): number => {
      const a = 1;
      const p = 0.4;
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      const s: number = p / 4;
      return a * 2 ** (-10 * k) * Math.sin(((k - s) * (2 * Math.PI)) / p) + 1;
    },
    InOut: (k: number): number => {
      const a = 1;
      const p = 0.4;
      if (k === 0) {
        return 0;
      }
      if (k === 1) {
        return 1;
      }
      const s: number = p / 4;
      if ((k *= 2) < 1) {
        return -0.5 * (a * 2 ** (10 * (k -= 1)) * Math.sin(((k - s) * (2 * Math.PI)) / p));
      } // tslint:disable-line
      return a * 2 ** (-10 * (k -= 1)) * Math.sin(((k - s) * (2 * Math.PI)) / p) * 0.5 + 1;
    },
  };

  public static Back: TweenType = {
    In: (k: number): number => {
      const s = 1.70158;
      return k * k * ((s + 1) * k - s);
    },
    Out: (k: number): number => {
      const s = 1.70158;
      return --k * k * ((s + 1) * k + s) + 1;
    },
    InOut: (k: number): number => {
      const s: number = 1.70158 * 1.525;
      if ((k *= 2) < 1) {
        return 0.5 * (k * k * ((s + 1) * k - s));
      } // tslint:disable-line
      return 0.5 * ((k -= 2) * k * ((s + 1) * k + s) + 2);
    },
  };

  public static Bounce: TweenType = {
    In: (k: number): number => 1 - Easing.Bounce.Out(1 - k),
    Out: (k: number): number => {
      if (k < 1 / 2.75) {
        return 7.5625 * k * k;
      }
      if (k < 2 / 2.75) {
        return 7.5625 * (k -= 1.5 / 2.75) * k + 0.75;
      }
      if (k < 2.5 / 2.75) {
        return 7.5625 * (k -= 2.25 / 2.75) * k + 0.9375;
      }
      return 7.5625 * (k -= 2.625 / 2.75) * k + 0.984375;
    },
    InOut: (k: number): number => {
      if (k < 0.5) {
        return Easing.Bounce.In(k * 2) * 0.5;
      }
      return Easing.Bounce.Out(k * 2 - 1) * 0.5 + 0.5;
    },
  };
}

Easing.Default = Easing.Linear.None;
Easing.Power0 = Easing.Linear.None;
Easing.Power1 = Easing.Quadratic.Out;
Easing.Power2 = Easing.Cubic.Out;
Easing.Power3 = Easing.Quartic.Out;
Easing.Power4 = Easing.Quintic.Out;

export const EasingMap = {
  linear: Easing.Linear.None,
  quadIn: Easing.Quadratic.In,
  quadOut: Easing.Quadratic.Out,
  quadInOut: Easing.Quadratic.InOut,
  cubicIn: Easing.Cubic.In,
  cubicOut: Easing.Cubic.Out,
  cubicInOut: Easing.Cubic.InOut,
  quarticIn: Easing.Quartic.In,
  quarticOut: Easing.Quartic.Out,
  quarticInOut: Easing.Quartic.InOut,
  quinticIn: Easing.Quintic.In,
  quinticOut: Easing.Quintic.Out,
  quinticInOut: Easing.Quintic.InOut,
  sinIn: Easing.Sinusoidal.In,
  sinOut: Easing.Sinusoidal.Out,
  sinInOut: Easing.Sinusoidal.InOut,
  expIn: Easing.Exponential.In,
  expOut: Easing.Exponential.Out,
  expInOut: Easing.Exponential.InOut,
  circularIn: Easing.Circular.In,
  circularOut: Easing.Circular.Out,
  circularInOut: Easing.Circular.InOut,
  elasticIn: Easing.Elastic.In,
  elasticOut: Easing.Elastic.Out,
  elasticInOut: Easing.Elastic.InOut,
  backIn: Easing.Back.In,
  backOut: Easing.Back.Out,
  backInOut: Easing.Back.InOut,
  bounceIn: Easing.Bounce.In,
  bounceOut: Easing.Bounce.Out,
  bounceInOut: Easing.Bounce.InOut,
  pow0: Easing.Linear.None,
  pow1: Easing.Quadratic.Out,
  pow2: Easing.Cubic.Out,
  pow3: Easing.Quartic.Out,
  pow4: Easing.Quintic.Out,
  appear1: (v: number) => bezierInterpolation([0, 0.5, 0.5, 1.8, 1, 1], v),
  disappear1: (v: number) => bezierInterpolation([0, -0.3, 1], v),
};

export type EasingType = EasingFunction | keyof typeof EasingMap;
